import { useMemo, useState } from 'react'
import { Pencil, UserPlus } from 'lucide-react'
import type { NtBlock, NtCourse, NtOverride, StudentDoc } from '../../core/types'
import { useAuth } from '../../core/auth'
import { useRoster } from '../admin/model'
import { useNorseTerm } from './NorseTermData'
import { useOverrides, useNorseTermTeachers, createSection, createAllDaySection, updateSection, placeInBlock, placeInBlocks, type SectionInput } from './model'
import { Text } from '../../ui/Field'
import { Empty, ErrorState, Loading } from '../../ui/States'

/**
 * Credit recovery: sections admin sets up and staffs, and the students admin
 * puts in them. A section is a course with `assignedOnly`, approved from the
 * start and never in the catalog, so nobody can choose it. Students get there
 * by hand placement, which placement reserves before anyone else.
 *
 * Credit recovery students still make course selections for both blocks:
 * one who finishes early moves into a course, and their own picks for that
 * block are listed beside their name so admin can move them in one step.
 */
const blockName = (b: NtBlock | null) => (b === 'AM' ? 'Morning' : b === 'PM' ? 'Afternoon' : 'No block')

export function CreditRecovery() {
  const { profile, user } = useAuth()
  const { term, courses, students, placement } = useNorseTerm()
  const overrides = useOverrides(term.id, true)
  const { rows: roster } = useRoster(true)
  const teachers = useNorseTermTeachers(true)
  const [adding, setAdding] = useState(false)
  const who = profile?.displayName ?? user?.email ?? ''

  const sections = (courses ?? []).filter((c) => c.assignedOnly)
  // An all-day section is two linked halves, shown as one: morning, then afternoon.
  const units: NtCourse[][] = []
  for (const c of sections) {
    if (!c.pairId) { units.push([c]); continue }
    if (units.some((u) => u[0]!.pairId === c.pairId)) continue
    units.push(sections.filter((x) => x.pairId === c.pairId).sort((a, b) => (a.block === 'PM' ? 1 : 0) - (b.block === 'PM' ? 1 : 0)))
  }
  if (!courses || !roster) return <Loading label="Loading credit recovery" />

  return (
    <>
      <p className="ov__p" style={{ marginTop: 'var(--s-2)' }}>
        Make a section for the morning, the afternoon or all day, choose its teacher, then add students. They go straight onto that
        section&apos;s roster, and placement fills the other block from their course selections. Students never see
        credit recovery in the catalog. They still make course selections for both blocks, so anyone who finishes early
        can move into a course they picked. Students in credit recovery all day make no open-house artifact.
      </p>
      {sections.length === 0 && !adding && (
        <Empty title="No credit recovery sections yet" body="Make one for the morning, the afternoon, or both." />
      )}
      {units.map((u) => (
        <Section key={u[0]!.id} cs={u} roster={roster} teachers={teachers} overrides={overrides} who={who}
          courses={courses} students={students ?? []} placement={placement} termId={term.id} />
      ))}
      {adding
        ? <SectionForm teachers={teachers} onDone={() => setAdding(false)}
            onSave={(x) => (x.allDay ? createAllDaySection(term.id, x) : createSection(term.id, x))} />
        : <button className="plan__add" style={{ marginTop: 'var(--s-3)' }} onClick={() => setAdding(true)}>+ New credit recovery section</button>}
    </>
  )
}

type SectionProps = {
  roster: StudentDoc[]; overrides: NtOverride[]; who: string
  courses: NtCourse[]; students: ReturnType<typeof useNorseTerm>['students'] & object
  placement: ReturnType<typeof useNorseTerm>['placement']; termId: string
}

/** One section, or both halves of an all-day one, with its rosters and the way to add a student. */
function Section({ cs, teachers, ...p }: SectionProps & { cs: NtCourse[]; teachers: Array<{ email: string; name: string }> }) {
  const [editing, setEditing] = useState(false)
  const [q, setQ] = useState('')
  const [mode, setMode] = useState<'all' | NtBlock>('all')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const c = cs[0]!
  const allDay = cs.length === 2
  const byId = useMemo(() => new Map(p.courses.map((x) => [x.id, x])), [p.courses])
  const ranksOf = useMemo(() => new Set(p.students.map((s) => s.studentId)), [p.students])
  const keyOf = (b: NtBlock): 'amCourseId' | 'pmCourseId' => (b === 'AM' ? 'amCourseId' : 'pmCourseId')
  const target = cs.filter((x) => mode === 'all' || x.block === mode)
  const inAll = new Set(p.overrides.filter((o) => target.every((x) => o[keyOf(x.block as NtBlock)] === x.id)).map((o) => o.studentId))
  const matches = q.trim().length < 2 ? [] : p.roster
    .filter((s) => s.active !== false && !inAll.has(s.studentId) && s.displayName.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 8)
  const add = async (sid: string) => {
    setBusy(true); setErr(null)
    try {
      const patch = Object.fromEntries(target.map((x) => [x.block as NtBlock, x.id]))
      await placeInBlocks(p.termId, sid, patch, undefined, p.overrides.find((o) => o.studentId === sid), p.placement ?? null, p.courses, p.students, p.who)
      setQ('')
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }

  if (editing) {
    return <SectionForm teachers={teachers} existing={c} allDay={allDay} onDone={() => setEditing(false)}
      onSave={(x) => Promise.all(cs.map((half) => updateSection(half, x)))} />
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h2 className="ov__h" style={{ margin: 0 }}>{c.title} <span className="pill">{allDay ? 'All day' : blockName(c.block)}</span></h2>
          <div className="work__sub">{c.teacherName || c.teacherEmail}{c.room ? ` · ${c.room}` : ''} · {c.cap} seats{allDay ? ' each half' : ''}</div>
        </div>
        <button className="opt__icon" aria-label={`Edit ${c.title}`} onClick={() => setEditing(true)}><Pencil size={15} aria-hidden="true" /></button>
      </div>
      {!c.teacherUid && (
        <div className="notice notice--warning" style={{ marginTop: 'var(--s-2)' }}>
          <span aria-hidden="true">⚠</span>
          <div>{c.teacherName || c.teacherEmail} hasn&apos;t opened Norse Term since this was set up. It becomes theirs (roster and
            attendance) the next time they do.</div>
        </div>
      )}
      {err && <ErrorState body={`Not saved. ${err}`} />}

      {cs.map((half) => <HalfRoster key={half.id} c={half} showBlock={allDay} {...p} />)}

      <div style={{ marginTop: 'var(--s-4)' }}>
        {allDay && (
          <div className="chips" role="tablist" aria-label="Add them for" style={{ marginBottom: 'var(--s-2)' }}>
            {([['all', 'All day'], ['AM', 'Morning only'], ['PM', 'Afternoon only']] as const).map(([k, label]) => (
              <button key={k} role="tab" aria-selected={mode === k} className={`chip${mode === k ? ' is-on' : ''}`} onClick={() => setMode(k)}>{label}</button>
            ))}
          </div>
        )}
        <label className="sel__search" style={{ maxWidth: 360 }}>
          <UserPlus size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Add a student: type a name" aria-label={`Add a student to ${c.title}`}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {matches.length > 0 && (
          <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
            {matches.map((s) => {
              const o = p.overrides.find((x) => x.studentId === s.studentId)
              const elsewhere = target.map((x) => o?.[keyOf(x.block as NtBlock)]).find((id) => id && !cs.some((h) => h.id === id))
              return (
                <li key={s.email} className="work__row">
                  <button className="row-link" disabled={busy} onClick={() => void add(s.studentId)}>{s.displayName}</button>
                  <span className="work__sub" style={{ marginLeft: 'auto' }}>
                    {elsewhere ? `Placed by hand in ${byId.get(elsewhere)?.title ?? 'another course'}; this replaces it`
                      : !ranksOf.has(s.studentId) ? 'No course selections yet: placement skips them until they make some' : ''}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

/** One block's roster: each student, their own picks for this block, and "finished early". */
function HalfRoster({ c, showBlock, roster, overrides, who, courses, students, placement, termId }: SectionProps & { c: NtCourse; showBlock: boolean }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const block = c.block as NtBlock
  const key = block === 'AM' ? 'amCourseId' : 'pmCourseId'
  const byStudentId = useMemo(() => new Map(roster.map((s) => [s.studentId, s])), [roster])
  const ranksOf = useMemo(() => new Map(students.map((s) => [s.studentId, s.ranks])), [students])
  const byId = useMemo(() => new Map(courses.map((x) => [x.id, x])), [courses])
  const inIt = overrides.filter((o) => o[key] === c.id)
  const regular = courses.filter((x) => x.block === block && x.status === 'approved' && !x.assignedOnly)
  // Their own picks for this block, in their order, then every other course.
  const picksFor = (sid: string) => (ranksOf.get(sid) ?? []).filter((id) => byId.get(id)?.block === block && !byId.get(id)?.assignedOnly)
  const run = (id: string, fn: () => Promise<unknown>) => async () => {
    setBusy(id); setErr(null)
    try { await fn() } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(null) }
  }
  const put = (sid: string, courseId: string | null) =>
    placeInBlock(termId, sid, block, courseId, undefined, overrides.find((o) => o.studentId === sid), placement ?? null, courses, students, who)
  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      {showBlock && <h3 className="opt__label" style={{ margin: 0 }}>{blockName(block)} · {inIt.length} of {c.cap}</h3>}
      {!showBlock && <div className="work__sub">{inIt.length} of {c.cap} seats taken</div>}
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {inIt.length === 0 ? <p className="muted-note" style={{ marginTop: 'var(--s-1)' }}>No students yet.</p> : (
        <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
          {inIt.map((o) => {
            const s = byStudentId.get(o.studentId)
            const picks = picksFor(o.studentId)
            const rest = regular.filter((x) => !picks.includes(x.id))
            return (
              <li key={o.studentId} className="work__row" style={{ flexWrap: 'wrap' }}>
                <div className="work__main">
                  <div style={{ fontWeight: 610 }}>{s?.displayName ?? o.studentId}</div>
                  <div className="work__sub">
                    {picks.length
                      ? `Their ${blockName(block).toLowerCase()} picks: ${picks.map((id, i) => `${i + 1}. ${byId.get(id)?.title ?? id}`).join(', ')}`
                      : 'No course selections yet'}
                  </div>
                </div>
                <select className="opt__input" style={{ maxWidth: 260 }} value="" disabled={busy === o.studentId}
                  aria-label={`Move ${s?.displayName ?? o.studentId} out of credit recovery, ${blockName(block).toLowerCase()}`}
                  onChange={(e) => { const v = e.target.value; if (v) void run(o.studentId, () => put(o.studentId, v === '-' ? null : v))() }}>
                  <option value="">{busy === o.studentId ? 'Saving…' : 'Finished early: move to…'}</option>
                  {picks.length > 0 && <optgroup label="Their picks">{picks.map((id, i) => <option key={id} value={id}>{i + 1}. {byId.get(id)?.title}</option>)}</optgroup>}
                  {rest.length > 0 && <optgroup label="Other courses">{rest.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</optgroup>}
                  {!placement && <option value="-">Take out (placement decides)</option>}
                </select>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function SectionForm({ teachers, existing, allDay, onSave, onDone }: {
  teachers: Array<{ email: string; name: string }>
  existing?: NtCourse
  allDay?: boolean
  onSave: (x: SectionInput & { allDay: boolean }) => Promise<unknown>
  onDone: () => void
}) {
  const [f, setF] = useState({
    title: existing?.title ?? 'Credit recovery', block: (existing?.block ?? 'AM') as NtBlock | 'ALL',
    teacherEmail: existing?.teacherEmail ?? '', room: existing?.room ?? '', cap: String(existing?.cap ?? 15), desc: existing?.desc ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const sorted = [...teachers].sort((a, b) => a.name.localeCompare(b.name))
  const cap = parseInt(f.cap, 10)
  const ok = f.title.trim() && f.teacherEmail && cap > 0
  const save = async () => {
    setBusy(true); setErr(null)
    try {
      const t = teachers.find((x) => x.email === f.teacherEmail)
      await onSave({ ...f, block: f.block === 'ALL' ? 'AM' : f.block, allDay: f.block === 'ALL', cap, teacherName: t?.name ?? existing?.teacherName ?? f.teacherEmail })
      onDone()
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-3)' }}>
      <h2 className="ov__h" style={{ marginTop: 0 }}>{existing ? 'Edit section' : 'New credit recovery section'}</h2>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <Text label="Name" value={f.title} onChange={(v) => setF((x) => ({ ...x, title: v }))} required />
      {!existing ? (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-3)' }}>
          <legend className="opt__label" style={{ padding: 0 }}>Block</legend>
          <div className="chips">
            {(['AM', 'PM', 'ALL'] as const).map((b) => (
              <button key={b} type="button" aria-pressed={f.block === b} className={`chip${f.block === b ? ' is-on' : ''}`}
                onClick={() => setF((x) => ({ ...x, block: b }))}>{b === 'ALL' ? 'All day' : blockName(b)}</button>
            ))}
          </div>
        </fieldset>
      ) : <p className="opt__hint">{allDay ? 'All day: changes apply to the morning and the afternoon.' : `${blockName(existing.block)}. To change the block, make a new section.`}</p>}
      <label className="opt__label" htmlFor="cr-teacher">Teacher <span className="req">*</span></label>
      <select id="cr-teacher" className="opt__input" style={{ margin: '4px 0 2px' }} value={f.teacherEmail}
        onChange={(e) => setF((x) => ({ ...x, teacherEmail: e.target.value }))}>
        <option value="">Choose…</option>
        {sorted.map((t) => <option key={t.email} value={t.email}>{t.name}</option>)}
      </select>
      <p className="opt__hint" style={{ marginTop: 0 }}>Not listed? Give them the Teacher role on Norse Term staff and roles.</p>
      <div className="host__grid">
        <Text label="Room" value={f.room} onChange={(v) => setF((x) => ({ ...x, room: v }))} />
        <Text label="Seats" type="number" min={1} value={f.cap} onChange={(v) => setF((x) => ({ ...x, cap: v }))} required />
      </div>
      <Text label="Note for the teacher (optional)" value={f.desc} onChange={(v) => setF((x) => ({ ...x, desc: v }))} />
      <div className="opt__bar">
        <button className="btn" disabled={busy || !ok} onClick={save}>{busy ? 'Saving…' : existing ? 'Save' : 'Create section'}</button>
        <button className="btn btn--ghost" disabled={busy} onClick={onDone}>Cancel</button>
      </div>
    </section>
  )
}
